import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { CategoryToGlobalProjectIssueResourceType } from '../../../../shared/issuesV2/permissions';
import { Action } from '../../../../typings/permissions';
import { window } from '../../../globals';
import {
  userHasPermissionForProjectIssues,
  userHasPermissionForReleaseGroupIssues,
} from '../../../helpers/permissions';
import useOnMount from '../../../hooks/useOnMount';
import CommentStore from '../../../redux/comments';
import { getCommentsByProjectId } from '../../../redux/comments/selectors';
import Button from '../Button/Button';
import ButtonGroup from '../Button/ButtonGroup';
import Loader from '../Loader';
import type { Category } from '../../../../shared/issuesV2';
import type { IssueScanScope } from '../../../../typings/api';
import type { ReduxStore } from '../../../redux/store';

import CommentItem from './CommentItem';

type CommentPanelProps = {
  projectId: string;
  scanScope: IssueScanScope;
  issueCategory: Category;
  disableComments?: boolean;
};

const CommentPanel: VoidFunctionComponent<CommentPanelProps> = ({
  projectId,
  scanScope,
  issueCategory,
  disableComments = false,
}) => {
  const dispatch = useDispatch();
  const status = useSelector(CommentStore.selectors.getStatus);
  const comments = useSelector((state: ReduxStore) => getCommentsByProjectId(state, projectId));
  const [newCommentBody, setNewCommentBody] = useState('');
  const hasErrors = status.errors.length > 0;

  useOnMount(() => {
    dispatch(CommentStore.actions.fetchAll(projectId));
  });

  // We want to re-fetch whenever the projectId changes.
  useEffect(() => {
    dispatch(CommentStore.actions.fetchAll(projectId));
  }, [dispatch, projectId]);

  const onSubmit = useCallback(async () => {
    dispatch(
      CommentStore.actions.create(projectId, {
        body: newCommentBody,
        projectId,
      }),
    );
    setNewCommentBody('');
  }, [dispatch, newCommentBody, projectId]);

  const canComment = useCallback(() => {
    if (scanScope.type === 'releaseGroup') {
      return userHasPermissionForReleaseGroupIssues(
        scanScope.groupId,
        issueCategory,
        Action.Comment,
      );
    }
    if (scanScope.type === 'project') {
      return userHasPermissionForProjectIssues(
        scanScope.projectId,
        issueCategory,
        Action.Comment,
      );
    }
    return window.hasPermission({
      resourceType: CategoryToGlobalProjectIssueResourceType[issueCategory],
      action: Action.Comment,
    });
  }, [issueCategory, scanScope]);

  const onUpdateComment = useCallback((commentId: number, updatedCommentBody: string) => {
    dispatch(CommentStore.actions.update(commentId, projectId, {
      body: updatedCommentBody,
      projectId,
    }));
  }, [dispatch, projectId]);

  const onDeleteComment = useCallback((commentId: number) => {
    dispatch(CommentStore.actions.delete(commentId, projectId));
  }, [dispatch, projectId]);

  return (
    <div className="comment-panel">
      {!disableComments && canComment() && (
        <div className="comment-box">
          <textarea
            placeholder="Click here to leave a comment"
            value={newCommentBody}
            onChange={(ev) => setNewCommentBody(ev.target.value)}
            disabled={status.loading || hasErrors}
          />
          <div className="comment-box-footer">
            <ButtonGroup>
              <Button
                type="secondary"
                size="medium"
                onClick={() => setNewCommentBody('')}
                isDisabled={status.loading || hasErrors || !newCommentBody}
              >
                Clear
              </Button>
              <Button
                type="primary"
                size="medium"
                onClick={onSubmit}
                isDisabled={status.loading || hasErrors || !newCommentBody}
              >
                Submit
              </Button>
            </ButtonGroup>
          </div>
        </div>
      )}
      {status.errors.length > 0
        ? status.errors.map((err: string) => (
          <div className="alert alert-error">{err}</div>
        ))
        : ''}
      {status.loading ? (
        <Loader />
      ) : (
        <div>
          {comments && comments.length === 0 ? (
            <div className="padded text-center">
              <i>No comments yet.</i>
            </div>
          ) : null}
          {comments.map((comment) => {
            return (
              <CommentItem
                onUpdateComment={onUpdateComment}
                onDeleteComment={onDeleteComment}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...comment}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommentPanel;
